import { computed, Injectable, signal, Signal } from "@angular/core";
import { Area, Assertion, AssertionIssue, Component, db, ImageType, InspectionJob, JobIssue, JobIssueVendor, Lot, Media, Zone } from "../../../core/database/db";
import { BqStepDataService } from "../../../personas/shared/bq-step-data-service/bq-step-data-service-factory";
import { AreaConfigurationDTO } from "../../areas/models/configuration/AreaConfigurationDTO";
import { EditRoomNameUpdate } from "../components/job-areas/edit-rooms/edit-room-names.component";
import { LoggerService } from "../../../shared/services/logger.service";
import { AddAreaSubmit } from "../components/job-areas/add-area/add-area-dialog.component";
import { AssertionIssuesService } from "../../assertionIssues/services/assertion-issues.service";
import { AssertionService } from "../../assertions/services/assertion.service";
import { AreaService } from "../../areas/services/area.service";
import JobDataService from "./job-data-service";
import { ComponentConfigurationDTO } from "../../areas/models/configuration/ComponentConfigurationDTO";
import { ComponentService } from "../../components/services/component.service";
import { JobIssueService } from "../../jobIssues/services/job-issue-service";
import { JobService } from "./job.service";
import { VendorDTO } from "../../vendors/models/VendorDTO";
import { VendorDataHelperService } from "../../vendors/services/vendor-data-helper.service";

export interface AreaWithDetails extends Area {
  components: Component[];
  assertions: Assertion[];
  issues: AssertionIssue[];
  zone: Zone;
  assertionCount: number;
  completedAssertions: number;
  hasIssues: boolean,
  started: boolean,
  completed: boolean,
  sortingOrder?: number,
  isCcmInspection: boolean
}

export type AssertionChanges = Pick<Assertion, "isApplicable"> & {
  isComplete?: boolean;
  isCcmComplete?: boolean;
  note?: string;
  ccmNote?: string;
};

@Injectable()
export class BqInspectionDataService implements BqStepDataService {

  constructor(
    private readonly logger: LoggerService,
    protected readonly assertionIssuesService: AssertionIssuesService,
    protected readonly assertionService: AssertionService,
    protected readonly areaService: AreaService,
    private readonly jobService: JobService,
    private readonly jobDataService: JobDataService,
    private readonly componentService: ComponentService,
    private readonly jobIssueService: JobIssueService,
    private readonly vendorDataHelperService: VendorDataHelperService
  ) { }

  readonly job = signal<InspectionJob | undefined>(undefined);
  readonly lot = signal<Lot | undefined>(undefined);
  readonly areas = signal<Area[] | undefined>(undefined);
  readonly jobIssues = signal<JobIssue[] | undefined>(undefined);
  readonly media = signal<Media[] | undefined>(undefined);
  readonly areasConfig = signal<AreaConfigurationDTO[] | undefined>(undefined);
  readonly zones = signal<Zone[] | undefined>(undefined);
  readonly assertionIssues = signal<AssertionIssue[] | undefined>(undefined);
  readonly components = signal<Component[] | undefined>(undefined);
  readonly assertions = signal<Assertion[] | undefined>(undefined);
  readonly isCcmInspection = signal<boolean>(false);
  readonly vendors = signal<VendorDTO[] | undefined>(undefined);
  readonly jobIssueVendors = signal<JobIssueVendor[] | undefined>(undefined);

  readonly bqStep = computed(() => `BQ${this.job()?.buildQualityStep ?? ""}`);

  /** Lot rooms available for assigning a new issue to, beyond this BQ step's own scoped `areas`. Only
   * populated by the steps that let the CM add a room mid-inspection (BQ1/BQ3/BQ4/BQ5, not BQ2/BQ8). */
  readonly availableRooms = signal<Area[] | undefined>(undefined);
  /** Every zone on the lot, for the Add a Room sheet; only populated alongside `availableRooms`. */
  readonly lotZones = signal<Zone[]>([]);

  /** Open issues from the preceding BQ step carried into the current one (e.g. BQ6 -> BQ7), keyed by assertionIdentifier. */
  readonly carryoverIssues = signal<AssertionIssue[]>([]);
  /** Issue photos belonging to the carried-over issues (parentIdentifier === carried-over issue identifier). */
  readonly carryoverMedia = signal<Media[]>([]);
  readonly carryoverVendors = signal<JobIssueVendor[]>([]);
  /** Carried-over issue identifiers the inspector chose NOT to bring forward (left unchecked in the stepper). */
  readonly dismissedCarryoverIssueIdentifiers = signal<Set<string>>(new Set<string>());

  isRefreshingInBackground: Signal<boolean> = signal(false);
  dataUpdatedPulse: Signal<boolean> = signal(false);

  readonly hasData = computed(() => {
    return !!this.job() && !!this.lot() && !!this.areas() && !!this.jobIssues() && !!this.zones() && !!this.media() && !!this.assertionIssues() && !!this.components() && !!this.assertions();
  });

  async updateAreasNaStatus(areaIds: number[], areMarkedAreaIdsApplicable: boolean): Promise<void> {

    const { affectedAreaIds, affectedAssertionIds } = await this.areaService.updateAreaNotApplicableStatus(
      areaIds,
      areMarkedAreaIdsApplicable,
      this.components() ?? [],
      this.assertions() ?? []
    );

    this.areas.update(areas => (areas ?? []).map(area => {
      if (!area.id || !affectedAreaIds.includes(area.id)) {
        return area;
      }

      return { ...area, isApplicable: areMarkedAreaIdsApplicable };
    }));

    this.assertions.update(assertions => (assertions ?? []).map(assertion => {
      if (!assertion.id || !affectedAssertionIds.includes(assertion.id)) {
        return assertion;
      }

      return { ...assertion, isApplicable: areMarkedAreaIdsApplicable };
    }));
  }

  async updateAreaNames(updates: EditRoomNameUpdate[]) {
    const updatedAt = new Date();

    this.areaService.updateAreas(updates);

    const updatedNamesByAreaId = updates.reduce((dict, update) => {
      dict[update.id] = update.name;
      return dict;
    }, {} as { [key: number]: string });

    this.areas.update(areas => (areas ?? []).map(area => {
      if (!area.id || !updatedNamesByAreaId[area.id]) {
        return area;
      }

      return {
        ...area,
        name: updatedNamesByAreaId[area.id],
        updatedAt
      };
    }));
  }

  async addNewAreaToZone(areaDetails: AddAreaSubmit, zoneId: number): Promise<number | undefined> {
    if (!this.areasConfig()) {
      this.logger.error("[BqInspectionDataService:addNewAreaToZone] Area configuration is not loaded.");
      return;
    }

    const newAreaConfig = this.areasConfig()!.find(config => config.id === areaDetails.areaConfigId);

    if (!newAreaConfig) {
      this.logger.error(`[BqInspectionDataService:addNewAreaToZone] No area configuration found for id ${areaDetails.areaConfigId}.`);
      return;
    }

    const addAreaResult = await this.areaService.addAreaToZoneForJob(areaDetails.name, newAreaConfig, zoneId, this.job()!.id!);
    const { newArea, newComponents, newAssertions } = addAreaResult;

    this.assertions.update(assertions => [...(assertions ?? []), ...newAssertions]);
    this.components.update(components => [...(components ?? []), ...newComponents]);
    this.areas.update(areas => [...(areas ?? []), newArea]);

    return newArea.id;
  }

  /**
   * Adds a room from the BQ3/BQ4 Needs Attention flow or the BQ1/BQ5 punchlist so an item can be
   * assigned to it. Unlike `addNewAreaToZone`, the room is added to `availableRooms`, not this step's
   * own scoped `areas`.
   */
  async addNewRoomForIssueAssignment(areaDetails: AddAreaSubmit): Promise<Area> {
    const newAreaConfig = this.areasConfig()?.find(config => config.id === areaDetails.areaConfigId);
    if (!newAreaConfig) {
      throw new Error(`No area configuration found for id ${areaDetails.areaConfigId}.`);
    }

    if (!areaDetails.zoneId) {
      throw new Error("A zone is required to add a room.");
    }

    const { newArea } = await this.areaService.addAreaToZoneForJob(areaDetails.name, newAreaConfig, areaDetails.zoneId, this.job()!.id!);

    this.availableRooms.update(rooms => [...(rooms ?? []), newArea]);

    return newArea;
  }

  async addNewComponentToArea(newComponentDetail: ComponentConfigurationDTO, areaId: number): Promise<{ addedComponent: Component, addedAssertions: Assertion[] }> {

    const { addedComponent, addedAssertions } = await this.componentService.addComponentToAreaForJob(newComponentDetail, areaId, this.job()!.id!);

    this.assertions.update(assertions => [...(assertions ?? []), ...addedAssertions]);
    this.components.update(components => [...(components ?? []), addedComponent]);

    return { addedComponent, addedAssertions };
  }

  async deleteIssueForAssertion(issue: AssertionIssue): Promise<void> {
    this.assertionIssuesService.deleteIssue(issue.id!, issue.assertionIdentifier);

    this.assertionIssues.update(issues => (issues ?? []).filter(i => i.id !== issue.id));
    this.media.update(mediaList => (mediaList ?? []).filter(m => m.parentIdentifier !== issue.identifier));
  }

  async saveLooksGoodAssertionDetail(assertion: Assertion, note: string, media: Media[]): Promise<void> {
    const mediaFileIdentifierToIdMap = await this.assertionService.saveLooksGoodAssertionDetail(assertion, note, media, this.isCcmInspection());

    const updatedAt = new Date();
    this.assertions.update(assertions => (assertions ?? []).map(a => {
      const updated = { ...a, updatedAt };
      if (this.isCcmInspection()) {
        updated.ccmNote = note;
      } else {
        updated.note = note;
      }

      return (a.id === assertion.id ? updated : a);
    }));

    const newMedia = media.filter(m => !m.id).map(m => ({
      ...m,
      id: mediaFileIdentifierToIdMap[m.fileIdentifier]
    }));
    const deletedMediaIds = new Set(media.filter(m => m.deleted === 1 && m.id !== null && m.id !== undefined).map(m => m.id!));
    this.media.update(mediaList => [
      ...(mediaList ?? []).filter(m => !m.id || !deletedMediaIds.has(m.id)),
      ...newMedia
    ]);
  }

  async removeLooksGoodStatus(assertion: Assertion): Promise<void> {
    await this.assertionService.removeLooksGoodStatus(assertion, this.isCcmInspection());

    const updatedAt = new Date();
    this.assertions.update(assertions => (assertions ?? []).map(a => {
      const updated = {
        ...a,
        updatedAt
      };

      if (this.isCcmInspection()) {
        updated.isCcmComplete = false;
        updated.ccmNote = "";
      } else {
        updated.isComplete = false;
        updated.note = "";
      }

      return (a.id === assertion.id ? updated : a);
    }));
    this.media.update(mediaList => (mediaList ?? []).filter(m => m.parentIdentifier !== assertion.identifier));
  }

  /** Photos taken alongside a Pass hang off the assertion itself, tagged for whoever is inspecting. */
  looksGoodImageType(): ImageType {
    return this.isCcmInspection() ? ImageType.LooksGoodCustomerCareManager : ImageType.LooksGoodConstructionManager;
  }

  /** Photos attached to a passed checklist item. */
  getLooksGoodMediaForAssertion(assertionIdentifier: string): Media[] {
    const looksGoodImageType = this.looksGoodImageType();
    return (this.media() ?? []).filter(media =>
      media.parentIdentifier === assertionIdentifier && !media.deleted && media.imageType === looksGoodImageType
    );
  }

  async updateIssueSeverityForAssertion(issueId: number, severity: string): Promise<void> {
    await this.assertionIssuesService.updateIssueSeverity(issueId, severity);

    const updatedAt = new Date();
    this.assertionIssues.update(issues => (issues ?? []).map(i =>
      (i.id === issueId ? { ...i, severity, updatedAt } : i)
    ));
  }

  async updateIssueForAssertion(issueId: number, text: string, severity: string, media: Media[], issueIdentifier: string, selectedVendor: VendorDTO | null, dueDate: Date | null, lotAreaIdentifier: string | null): Promise<void> {
    const existingIssue = (this.assertionIssues() ?? []).find(i => i.id === issueId);
    if (existingIssue && this.assertionIssuesService.isAcceptedCarryoverIssue(existingIssue)) {
      throw new Error("Accepted carryover issues can only have their severity updated.");
    }

    const issueToAssign = existingIssue ?? ({ identifier: issueIdentifier, isSelfAssigned: false } as AssertionIssue);
    const previousVendorAssignment = this.getVendorAssignmentForIssue(issueToAssign);

    const mediaFileIdentifierToIdMap = await db.transaction("rw", [db.assertionIssue, db.media, db.jobIssueVendor, db.syncStatus], async () => {
      const mediaFileIdentifierToIdMap = await this.assertionIssuesService.updateIssue(issueId, text, severity, media, dueDate, lotAreaIdentifier);
      await this.addOrReplaceVendorAssignmentForAssertionIssue(issueToAssign, selectedVendor, previousVendorAssignment);

      return mediaFileIdentifierToIdMap;
    });

    const updatedAt = new Date();
    this.assertionIssues.update(issues => (issues ?? []).map(i =>
      (i.id === issueId ? { ...i, text, severity, updatedAt, dueDate: dueDate ?? undefined } : i)
    ));

    const newMedia = media.filter(m => !m.id).map(m => ({
      ...m,
      id: mediaFileIdentifierToIdMap[m.fileIdentifier]
    }));
    const deletedMediaIds = new Set(media.filter(m => m.deleted === 1 && m.id !== null && m.id !== undefined).map(m => m.id!));
    this.media.update(mediaList => [
      ...(mediaList ?? []).filter(m => !m.id || !deletedMediaIds.has(m.id)),
      ...newMedia
    ]);
  }

  async createIssueForAssertion(issue: AssertionIssue, media: Media[], selectedVendor: VendorDTO | null): Promise<void> {
    const mediaFileIdentifierToIdMap = await db.transaction("rw", [db.assertionIssue, db.media, db.jobIssueVendor, db.syncStatus], async () => {
      const mediaFileIdentifierToIdMap = await this.assertionIssuesService.createIssue(issue, media);

      this.assertionIssues.update(issues => [...(issues ?? []), issue]);
      await this.addOrReplaceVendorAssignmentForAssertionIssue(issue, selectedVendor, null);

      return mediaFileIdentifierToIdMap;
    });

    const newMedia = media.map(m => ({
      ...m,
      id: mediaFileIdentifierToIdMap[m.fileIdentifier],
      parentIdentifier: issue.identifier
    }));
    this.media.update(mediaList => [...(mediaList ?? []), ...newMedia]);
  }

  /** The assignment the vendor select shows for an issue (self-assignment, trade partner, or none). */
  getVendorAssignmentForIssue(issue: JobIssue): JobIssueVendor | null {
    return this.vendorDataHelperService.getVendorAssignmentForIssue(issue, this.jobIssueVendors());
  }

  /** Assigns a job issue to a trade partner or to the CM ("Assign to Me"), replacing the previous assignment. */
  addOrReplaceVendorAssignmentForIssue(
    issue: JobIssue,
    selectedVendor: VendorDTO | null,
    previousSelectedVendorAssignmentToReplace: JobIssueVendor | null
  ): Promise<JobIssueVendor | null> {
    return this.vendorDataHelperService.addOrReplaceVendorAssignmentForIssue(
      issue,
      selectedVendor,
      previousSelectedVendorAssignmentToReplace,
      this.jobIssueVendors,
      this.jobIssues
    );
  }

  /**
   * Assigns a checklist (assertion) issue to a trade partner or to the CM ("Assign to Me"), keeping the
   * step's `assertionIssues` signal in sync. On a CM step the Open Items item is this same server-side JobIssue
   * (same GUID), so the self-assignment reaches Open Items through the assertion issue sync.
   */
  addOrReplaceVendorAssignmentForAssertionIssue(
    issue: AssertionIssue,
    selectedVendor: VendorDTO | null,
    previousSelectedVendorAssignmentToReplace: JobIssueVendor | null
  ): Promise<JobIssueVendor | null> {
    return this.vendorDataHelperService.addOrReplaceVendorAssignmentForIssue(
      issue,
      selectedVendor,
      previousSelectedVendorAssignmentToReplace,
      this.jobIssueVendors,
      this.assertionIssues,
      this.assertionIssuesService
    );
  }

  /**
   * Returns the still-open carried-over issues for an assertion that have NOT yet been handled in the
   * current step (e.g. BQ6 issues during a BQ7 audit). An issue is considered handled once it has been
   * carried over (a current-step child issue references it via parentIdentifier) or the inspector
   * explicitly dismissed it (left it unchecked in the stepper).
   */
  getOpenCarryoverIssuesForAssertion(assertionIdentifier: string): AssertionIssue[] {
    const dismissed = this.dismissedCarryoverIssueIdentifiers();
    const clonedParentIdentifiers = new Set(
      (this.assertionIssues() ?? [])
        .filter(issue => !issue.deleted && !!issue.parentIdentifier)
        .map(issue => issue.parentIdentifier as string)
    );

    return this.carryoverIssues().filter(issue =>
      issue.assertionIdentifier === assertionIdentifier &&
      !dismissed.has(issue.identifier) &&
      !clonedParentIdentifiers.has(issue.identifier)
    );
  }

  /** Non-deleted issue photos for a carried-over issue. */
  getCarryoverMediaForIssue(issueIdentifier: string): Media[] {
    return this.carryoverMedia().filter(media =>
      media.parentIdentifier === issueIdentifier && !media.deleted
    );
  }

  /** Vendor assignments (with their sent/viewed state) to carry forward for a carried-over issue. */
  getCarryoverVendorsForIssue(issueIdentifier: string): JobIssueVendor[] {
    return this.carryoverVendors().filter(vendor =>
      vendor.issueIdentifier === issueIdentifier && !vendor.deleted
    );
  }

  /** Records that the inspector chose not to bring these carried-over issues forward. Keeps them off the badge. */
  dismissCarryoverIssues(issueIdentifiers: string[]): void {
    if (issueIdentifiers.length === 0) {
      return;
    }

    this.dismissedCarryoverIssueIdentifiers.update(current => {
      const next = new Set(current);
      issueIdentifiers.forEach(identifier => next.add(identifier));
      return next;
    });
  }

  /**
   * Confirms a carried-over issue as a real finding in the current step (e.g. BQ6 -> BQ7): clones it (and
   * its photos and vendor assignments) into a new assertion issue on the current job with the
   * inspector-selected severity, linking back to the originating record via parentIdentifier.
   */
  async confirmCarryoverIssueToCurrentStep(carryoverIssue: AssertionIssue, severity: string): Promise<AssertionIssue> {
    const currentJobId = this.job()!.id;

    const { newIssue, newMedia, newVendors } = await this.assertionIssuesService.cloneCarryoverIssueToCurrentStep(
      carryoverIssue,
      currentJobId,
      severity,
      new Date(),
      this.getCarryoverVendorsForIssue(carryoverIssue.identifier)
    );

    this.assertionIssues.update(issues => [...(issues ?? []), newIssue]);
    if (newMedia.length > 0) {
      this.media.update(mediaList => [...(mediaList ?? []), ...newMedia]);
    }
    if (newVendors.length > 0) {
      this.jobIssueVendors.update(vendors => [...(vendors ?? []), ...newVendors]);
    }

    return newIssue;
  }

  async startJob(): Promise<void> {
    const updatedJob = await this.jobDataService.startJob(this.job()!);

    this.job.set(updatedJob);
  }

  async updateAssertionState(assertion: Assertion, changes: AssertionChanges): Promise<Assertion> {
    const assertionToUpdate: Assertion = {
      ...assertion,
      ...changes
    };

    const updatedAssertion = await this.assertionService.updateAssertion(assertionToUpdate);

    this.assertions.update(assertions => (assertions ?? []).map(a =>
      (a.id === updatedAssertion.id ? updatedAssertion : a)
    ));

    return updatedAssertion;
  }

  /** The still-open issues logged against a checklist item, oldest first. */
  getIssuesForAssertion(assertionIdentifier: string): AssertionIssue[] {
    return (this.assertionIssues() ?? [])
      .filter(issue => issue.assertionIdentifier === assertionIdentifier && !issue.deleted)
      .sort((a, b) => a.createdAt.valueOf() - b.createdAt.valueOf());
  }

  /** Non-deleted photos attached to an issue. */
  getMediaForIssue(issueIdentifier: string): Media[] {
    return (this.media() ?? []).filter(media => media.parentIdentifier === issueIdentifier && !media.deleted);
  }

  async submitJob(job: InspectionJob, issuesToClone: AssertionIssue[]): Promise<void> {
    const updatedAt = new Date();

    await db.transaction("rw", [
      db.inspectionJob,
      db.jobIssue,
      db.jobIssueVendor,
      db.assertion,
      db.component,
      db.area,
      db.media,
      db.syncStatus,
    ], async () => {
      if (issuesToClone.length > 0) {
        await this.jobIssueService.cloneAssertionIssuesToChildJobIssues(issuesToClone, updatedAt);
      }

      await this.jobService.markJobAsComplete(job.id, updatedAt);
    });
  }
}


